import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CompanionPageTitle,
  CompanionPublicPage,
  CompanionSection,
  SupportEmail,
} from '../components/companion/CompanionPublicPage';
import {
  MAC_MIN_OS,
  detectVisitorOs,
  macArchLabel,
  type VisitorOs,
} from '../lib/companionDownload';
import { useDesktopReleases } from '../lib/useDesktopReleases';

/**
 * Public download page for HOWIE Companion on Mac and Windows. Loads without
 * signing in. Release links come from public/companion/desktop-releases.json;
 * an empty url renders a disabled "coming soon" button (not while the manifest loads).
 */
export default function CompanionDownloadPage() {
  const { releases, status } = useDesktopReleases();
  const loading = status === 'loading';
  const [os] = useState<VisitorOs>(() => detectVisitorOs(typeof navigator === 'undefined' ? undefined : navigator));

  // The visitor's own OS comes first as a button; the other is a text link. Elsewhere, both are buttons.
  const mac = (
    <DownloadOption
      key="mac"
      primary={os !== 'windows'}
      label="Download for Mac"
      url={releases.mac.url}
      requirement={loading ? MAC_MIN_OS : `${MAC_MIN_OS} · ${macArchLabel(releases.mac.arch)}`}
      loading={loading}
      version={releases.version}
      sha256={releases.mac.sha256}
    />
  );
  const windows = (
    <DownloadOption
      key="windows"
      primary={os !== 'mac'}
      label="Download for Windows"
      url={releases.windows.url}
      requirement="Windows 10 or 11, 64-bit"
      loading={loading}
      version={releases.version}
      sha256={releases.windows.sha256}
    />
  );
  // Windows shows once it has a build (Oct 19: Mac first; Windows is untested).
  const windowsOut = Boolean(releases.windows.url);
  const options = !windowsOut ? [mac] : os === 'windows' ? [windows, mac] : [mac, windows];

  return (
    <CompanionPublicPage>
      <CompanionPageTitle>Companion</CompanionPageTitle>
      <p className="mt-6 max-w-[680px] text-lg leading-8 text-how-space-mid">
        Howie beside your meetings in Zoom, Microsoft Teams, Slack and Google Meet. Ask it anything. Let it listen when you choose.
        When the meeting ends, read your take: what happened, what you committed to, and how you lived the HOW.
      </p>

      <section aria-label="Downloads" className="mt-10 space-y-6">
        {os === 'other' && (
          <p className="text-landing-muted">
            HOWIE Companion runs on {windowsOut ? 'Mac and Windows computers' : 'Mac computers'}. Open this page on one to install it.
          </p>
        )}
        {options}
        {releases.mac.intelUrl && (
          <p className="text-[13px] leading-5 text-how-space-mid opacity-80">
            On an Intel Mac?{' '}
            <a className="underline" href={releases.mac.intelUrl}>
              Download the Intel version
            </a>
            .
          </p>
        )}
        {(releases.mac.sha256 || releases.windows.sha256) && (
          <p className="text-sm text-landing-muted">
            To check a download before you open it, compare its SHA-256 with the one above: on a Mac, run{' '}
            <code>shasum -a 256</code> followed by the file in Terminal; on Windows, run <code>Get-FileHash</code> followed
            by the file in PowerShell.
          </p>
        )}
      </section>

      <CompanionSection title="Meetings in Chrome?">
        <p>
          Add the HOWIE Companion Chrome extension too. The app and the extension work together, so you keep one panel.
        </p>
      </CompanionSection>

      <CompanionSection title="What it asks for, and why">
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Microphone</strong>, to hear you. It keeps listening when you mute yourself in Zoom or Teams, so it
            also hears anyone in the room with you. Turn it off from the listening pill.
          </li>
          <li>
            <strong>System audio recording</strong> (Mac), to hear the meeting app, and only the meeting app.
          </li>
          <li>
            <strong>Accessibility</strong> (Mac, optional), to see when a call starts and who is speaking in Zoom and
            Teams. Howie reads the names from the meeting app&apos;s window; it does not record your screen.
          </li>
          <li>
            <strong>Control Google Chrome</strong> (Mac, optional), to notice a Google Meet, Zoom or Teams call in a Chrome
            tab and read who is speaking there, without the Chrome extension. Howie reads the meeting page; it never
            changes your tabs.
          </li>
          {windowsOut && (<li>
            <strong>On Windows</strong>, your microphone and the sound your computer plays. That can include other sounds,
            such as music or a video, not only the meeting. No administrator rights; it installs for you alone.
          </li>)}
        </ul>
        <p>Howie asks for each one the first time it needs it.</p>
      </CompanionSection>

      <CompanionSection title="Before your first meeting">
        <p>
          Howie doesn&apos;t announce itself in your meetings. Tell the people you meet with that you&apos;re using it.
          Howie gives you a line to paste. What it keeps, and for how long, is in the{' '}
          <Link className="underline" to="/companion/privacy">privacy policy</Link>.
        </p>
      </CompanionSection>

      {windowsOut && !releases.windows.signed && (
        <CompanionSection id="windows-run-anyway" title="Windows: if you see “Windows protected your PC”">
          <p>
            This pilot build isn&apos;t signed yet. Choose <strong>More info</strong>, then <strong>Run anyway</strong>. To
            check the file first, compare its SHA-256 with the one above.
          </p>
          <p>
            If Smart App Control is on, Windows blocks unsigned apps and offers no Run anyway. Wait for the signed build, or
            write to <SupportEmail />.
          </p>
        </CompanionSection>
      )}

      <CompanionSection title="Uninstall">
        <p>
          Mac: open Settings from the HOWIE Companion icon in the menu bar and choose{' '}
          <strong>Remove HOWIE Companion…</strong>. It removes the app and what it saved on this Mac.
        </p>
        <p>Windows: Settings › Apps › Installed apps › HOWIE Companion › Uninstall.</p>
        <p>Your sessions stay in your Howie account until you delete them in History.</p>
      </CompanionSection>

      <CompanionSection id="faq" title="Questions">
        <Faq question="Does Howie tell the others in my meeting?">
          No. Howie doesn&apos;t join as a bot or post anything. Telling people is yours to do, and in many places the law
          requires it. Howie gives you a line to paste.
        </Faq>
        <Faq question="Does Howie record my meetings?">
          It doesn&apos;t record or keep audio. It transcribes while it&apos;s on. It keeps the lines Howie cites in each
          answer, the names of the people in the meeting, and your take, until you delete them.
        </Faq>
        <Faq question="What if someone objects partway through?">
          Tap Stop and clear. Howie drops what it heard and writes no take.
        </Faq>
        <Faq question="My IT team has questions.">
          Send them the <Link className="underline" to="/companion/privacy#it-admins">FAQ for IT admins</Link>.
        </Faq>
      </CompanionSection>

      <p className="mt-12 text-sm">
        <Link className="underline" to="/companion/privacy">Privacy policy</Link> ·{' '}
        <Link className="underline" to="/companion/privacy#it-admins">FAQ for IT admins</Link> · <SupportEmail />
      </p>
    </CompanionPublicPage>
  );
}

function DownloadOption({
  primary,
  label,
  url,
  requirement,
  version,
  sha256,
  loading = false,
}: {
  primary: boolean;
  label: string;
  url: string;
  requirement: string;
  version: string;
  sha256: string;
  /** The manifest is still loading: no "coming soon" yet. */
  loading?: boolean;
}) {
  const available = Boolean(url);
  const details = [requirement, version ? `version ${version}` : null].filter(Boolean).join(' · ');
  return (
    <div className="space-y-2">
      {!primary ? (
        available ? (
          <a href={url} className="underline text-how-space-deep">
            {label}
          </a>
        ) : (
          <span className="text-how-space-deep">{label}</span>
        )
      ) : available ? (
        <a
          href={url}
          className="inline-block bg-how-space-deep text-white text-base px-6 py-3 rounded-full hover:bg-opacity-90 transition-all"
        >
          {label}
        </a>
      ) : (
        <button
          type="button"
          disabled
          className="inline-block bg-how-space-deep text-white text-base px-6 py-3 rounded-full opacity-50 cursor-not-allowed"
        >
          {label}
        </button>
      )}
      <p className="text-[13px] leading-5 text-how-space-mid opacity-80">
        {available || loading ? details : `${details} · coming soon`}
      </p>
      {available && sha256 && (
        <p className="text-sm text-landing-muted break-all">
          SHA-256 <code>{sha256}</code>
        </p>
      )}
    </div>
  );
}

function Faq({ question, children }: { question: string; children: React.ReactNode }) {
  return (
    <div className="border-t border-how-space-deep/10 pt-5 first:border-0 first:pt-0">
      <h3 className="font-medium text-how-space-deep">{question}</h3>
      <p className="mt-2">{children}</p>
    </div>
  );
}
